import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  startRegistration,
  startAuthentication,
} from '@simplewebauthn/browser';
import { ConfirmDialog, ConfirmDialogState } from '../Components/ConfirmDialog';
import { useCacheStore } from '../hooks/data/useCacheStore';
import {
  supabaseData,
  AUTH_EXPIRED_EVENT,
  resetAuthExpiredGuard,
  setRealtimeAuth,
  syncRealtimeAuthFromStorage,
} from '../utils/supabase-data-client';

interface User {
  id: string;
  username: string;
}

interface WebAuthnContextType {
  user: User | null;
  loading: boolean;
  /**
   * TRUE only during the one-time session-token validation on mount. This is
   * the page-load gate — distinct from `loading`, which also flips during
   * in-flight auth ceremonies (register/authenticate). AppContent must gate its
   * full-page spinner on THIS flag, not `loading`; gating on `loading` unmounts
   * <AuthPage/> mid-ceremony and throws away useSmartAuth's `view` state (the
   * root cause of the "bare form after a dismissed NotAllowedError" bug).
   */
  initializing: boolean;
  snackbar: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  };
  setSnackbar: (snackbar: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }) => void;
  register: (username: string, newDevice?: boolean) => Promise<void>;
  authenticate: (username: string, fromRegisterFlow?: boolean) => Promise<void>;
  /**
   * Submit a device-access request for this username from the current device.
   * Returns the captured source IP (when available) so the caller can tell the
   * user exactly what an admin will approve. Used by the smart-login router.
   */
  requestDeviceAccess: (
    username: string,
  ) => Promise<{ ok: boolean; sourceIp?: string; error?: string }>;
  logout: () => void;
}

const WebAuthnContext = createContext<WebAuthnContextType | undefined>(
  undefined,
);

const API_BASE =
  'https://qaqj2uc5pc.execute-api.us-east-2.amazonaws.com/prod/api';

export const WebAuthnProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Page-load gate only — set false exactly once, when the initial session
  // validation settles. Never flipped by ceremonies, so AuthPage stays mounted.
  const [initializing, setInitializing] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info',
  });
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    onCancel: undefined,
  });

  const cache = useCacheStore();

  const populateCache = useCallback(async () => {
    try {
      const [categoriesResult, bookmarksResult] = await Promise.all([
        supabaseData.from('bookmark_categories').select('*'),
        supabaseData.from('bookmarks').select('*'),
      ]);

      if (categoriesResult.data) {
        cache.setCategories(categoriesResult.data);
      }
      if (bookmarksResult.data) {
        cache.setBookmarks(bookmarksResult.data);
      }
    } catch {
      // Silently fail — cache population is best-effort
    }
  }, [cache]);

  useEffect(() => {
    console.log('WebAuthnContext: Checking for session token...');
    const token = localStorage.getItem('session_token');
    if (!token) {
      console.log('WebAuthnContext: No token found');
      setLoading(false);
      setInitializing(false);
      return;
    }

    console.log('WebAuthnContext: Token found, validating...');
    fetch(`${API_BASE}/session/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then((resp) => resp.json())
      .then((result) => {
        console.log('WebAuthnContext: Validation result:', result);
        if (result.valid) {
          resetAuthExpiredGuard();
          // Token is already in localStorage here — authenticate the Realtime
          // socket so live postgres_changes are delivered under RLS.
          syncRealtimeAuthFromStorage();
          setUser({ id: crypto.randomUUID(), username: result.username });
          console.log('WebAuthnContext: User logged in:', result.username);
        } else {
          localStorage.removeItem('session_token');
          console.log('WebAuthnContext: Token invalid, removed');
        }
        setLoading(false);
        setInitializing(false);
      })
      .catch((error) => {
        console.error('Session validation failed:', error);
        localStorage.removeItem('session_token');
        setLoading(false);
        setInitializing(false);
      });
  }, []);

  const register = async (username: string, newDevice = false) => {
    const normalizedUsername = username.toLowerCase();
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/webauthn/register/begin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: normalizedUsername, newDevice }),
      });

      if (!resp.ok) {
        const errorData = await resp
          .json()
          .catch(() => ({ error: 'Registration failed' }));
        setLoading(false);

        // A 403 on the new-device path means there's no approved device
        // request for this device yet. Instead of dead-ending on an error
        // snackbar that says "request access again" with no way to do so,
        // offer to actually send the device request.
        if (resp.status === 403 && newDevice) {
          setConfirmDialog({
            open: true,
            title: 'Approve This Device?',
            message:
              'This device isn\'t approved for registration yet. Request approval now? Once approved, tap "Use a new device" again to finish.',
            confirmText: 'Request Access',
            cancelText: 'Cancel',
            onConfirm: async () => {
              setConfirmDialog((prev) => ({ ...prev, open: false }));
              setLoading(true);
              try {
                const reqResp = await fetch(`${API_BASE}/device-request/send`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ username: normalizedUsername }),
                });
                const reqResult = await reqResp.json().catch(() => ({}));
                if (reqResp.ok) {
                  setSnackbar({
                    open: true,
                    message: reqResult.sourceIp
                      ? `Device request submitted from IP ${reqResult.sourceIp}. Approve it in the Admin panel (Device Access Requests), then tap "Use a new device" again.`
                      : 'Device request submitted. Once approved, tap "Use a new device" again to finish.',
                    severity: 'info',
                  });
                } else {
                  setSnackbar({
                    open: true,
                    message: reqResult.error || 'Request failed',
                    severity: 'error',
                  });
                }
              } catch {
                setSnackbar({
                  open: true,
                  message: 'Network error while requesting access',
                  severity: 'error',
                });
              } finally {
                setLoading(false);
              }
            },
            onCancel: () =>
              setConfirmDialog((prev) => ({ ...prev, open: false })),
          });
          return;
        }

        setSnackbar({
          open: true,
          message: errorData.error || 'Registration failed',
          severity: 'error',
        });
        return;
      }

      const options = await resp.json();

      // Check if user already exists (and not registering new device)
      if (options.existingUser && !newDevice) {
        setLoading(false);
        setConfirmDialog({
          open: true,
          title: 'Already Registered',
          message:
            "You're already registered! Would you like to log in on this device?",
          onConfirm: () => {
            setConfirmDialog({ ...confirmDialog, open: false });
            authenticate(normalizedUsername, true);
          },
        });
        return;
      }

      const attResp = await startRegistration(options);

      const verificationResp = await fetch(
        `${API_BASE}/webauthn/register/finish`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: normalizedUsername, attResp }),
        },
      );

      if (verificationResp.ok) {
        const result = await verificationResp.json();

        if (result.verified && result.sessionToken) {
          console.log('✅ Registration success! Saving token to localStorage');
          localStorage.setItem('session_token', result.sessionToken);
          console.log(
            '✅ Token saved. Value:',
            localStorage.getItem('session_token')?.substring(0, 20) + '...',
          );
          resetAuthExpiredGuard();
          // Authenticate the Realtime socket so live updates arrive under RLS.
          setRealtimeAuth(result.sessionToken);
          const newUser = {
            id: crypto.randomUUID(),
            username: normalizedUsername,
          };
          setUser(newUser);
          populateCache();

          if (!result.newUser && result.message) {
            setSnackbar({
              open: true,
              message: result.message,
              severity: 'success',
            });
          }
        }
      } else {
        const error = await verificationResp.json();
        setSnackbar({
          open: true,
          message: error.error || 'Registration failed',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      setSnackbar({
        open: true,
        message: 'Registration failed',
        severity: 'error',
      });
    }
    setLoading(false);
  };

  const authenticate = async (username: string, fromRegisterFlow = false) => {
    const normalizedUsername = username.toLowerCase();
    setLoading(true);
    console.log('authenticate() called', {
      username: normalizedUsername,
      fromRegisterFlow,
    });
    try {
      console.log('Fetching authenticate/begin...');
      const resp = await fetch(`${API_BASE}/webauthn/authenticate/begin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: normalizedUsername }),
      });
      const options = await resp.json();
      console.log('Got options from begin:', options);

      if (!resp.ok || options.error) {
        setSnackbar({
          open: true,
          message: options.error || 'Authentication failed',
          severity: 'error',
        });
        setLoading(false);
        return;
      }

      console.log('Calling startAuthentication...');
      const asseResp = await startAuthentication(options);
      console.log('startAuthentication success:', asseResp);

      console.log('Posting to authenticate/finish...');
      const verificationResp = await fetch(
        `${API_BASE}/webauthn/authenticate/finish`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: normalizedUsername, asseResp }),
        },
      );

      console.log('Finish response status:', verificationResp.status);
      if (verificationResp.ok) {
        const result = await verificationResp.json();
        console.log('Finish result:', result);

        if (result.verified && result.sessionToken) {
          console.log(
            '✅ Authentication success! Saving token to localStorage',
          );
          localStorage.setItem('session_token', result.sessionToken);
          console.log(
            '✅ Token saved. Value:',
            localStorage.getItem('session_token')?.substring(0, 20) + '...',
          );
          resetAuthExpiredGuard();
          // Authenticate the Realtime socket so live updates arrive under RLS.
          setRealtimeAuth(result.sessionToken);
          const authenticatedUser = {
            id: crypto.randomUUID(),
            username: normalizedUsername,
          };
          setUser(authenticatedUser);
          populateCache();
        } else {
          setSnackbar({
            open: true,
            message: `Auth failed: verified=${result.verified}`,
            severity: 'error',
          });
        }
      } else {
        const errorData = await verificationResp.json().catch(() => ({}));
        console.log('Finish error data:', errorData);
        setSnackbar({
          open: true,
          message: `Auth failed: ${errorData.error || verificationResp.status}`,
          severity: 'error',
        });
      }
    } catch (error: any) {
      const errorName = error?.name || 'UnknownError';
      const errorMessage = error?.message || String(error);
      console.error('Authentication error:', error);
      console.error('Error name:', errorName);
      console.error('Error message:', errorMessage);
      console.error('Error stack:', error?.stack);
      console.error('fromRegisterFlow:', fromRegisterFlow);

      // Presents the "new device" flow: request admin approval, or register now if approved.
      const openNewDeviceDialog = () => {
        setConfirmDialog({
          open: true,
          title: 'Use a New Device?',
          message:
            'No passkey was found for this device. If this is a new device, request admin approval, then register once approved.',
          confirmText: 'Request Access',
          cancelText: 'Register Now',
          onConfirm: async () => {
            setConfirmDialog((prev) => ({ ...prev, open: false }));
            setLoading(true);
            try {
              const resp = await fetch(`${API_BASE}/device-request/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: normalizedUsername }),
              });
              const result = await resp.json();
              if (resp.ok) {
                setSnackbar({
                  open: true,
                  message: result.sourceIp
                    ? `Request submitted from IP ${result.sourceIp}. Approve it in the Admin panel (Device Access Requests), then click "Register Now".`
                    : 'Request submitted. Once the admin approves, click "Register Now" to finish.',
                  severity: 'info',
                });
              } else {
                setSnackbar({
                  open: true,
                  message: result.error || 'Request failed',
                  severity: 'error',
                });
              }
            } catch {
              setSnackbar({
                open: true,
                message: 'Network error while requesting access',
                severity: 'error',
              });
            } finally {
              setLoading(false);
            }
          },
          onCancel: () => {
            setConfirmDialog((prev) => ({ ...prev, open: false }));
            register(normalizedUsername, true);
          },
        });
      };

      setLoading(false);

      // Smart-login path (useSmartAuth) owns user-facing messaging via a
      // PERSISTENT inline Alert. When authenticate() is driven by that router
      // (fromRegisterFlow === false), do NOT also fire an auto-hide snackbar —
      // that produced a duplicate, self-dismissing banner. Rethrow so the
      // router can classify the error and show the persistent message. Only the
      // legacy "Already Registered" dialog path (fromRegisterFlow === true)
      // keeps the snackbar-based messaging below.
      if (!fromRegisterFlow) {
        throw error;
      }

      switch (errorName) {
        case 'NotAllowedError':
          // Ambiguous by design: covers user cancellation, timeout, AND no-credential.
          // Most often this is a cancelled/timed-out prompt — do NOT assume a new device.
          // Offer a clear retry, and make the new-device path an explicit opt-in.
          setSnackbar({
            open: true,
            message:
              'Login was cancelled or timed out. Tap Login to try again, or "Use a new device" if this device has no passkey yet.',
            severity: 'info',
          });
          break;

        case 'SecurityError':
          // rpId / origin mismatch (e.g. apex vs www, or wrong domain). Actionable diagnostic.
          setSnackbar({
            open: true,
            message: `Login blocked by a domain/origin mismatch (${errorMessage}). Make sure you're on the canonical site URL.`,
            severity: 'error',
          });
          break;

        case 'InvalidStateError':
          // During authentication this is uncommon; surface it plainly rather than mislabeling as "new device".
          setSnackbar({
            open: true,
            message: `Authenticator reported an invalid state: ${errorMessage}`,
            severity: 'error',
          });
          break;

        case 'AbortError':
          setSnackbar({
            open: true,
            message: 'Login was aborted. Please try again.',
            severity: 'info',
          });
          break;

        default:
          // Genuine/unknown failure. Show the error and offer the new-device path,
          // since a true "no credential on this device" can land here.
          setSnackbar({
            open: true,
            message: `Login failed (${errorName}): ${errorMessage}`,
            severity: 'error',
          });
          openNewDeviceDialog();
          break;
      }
      return;
    }
    setLoading(false);
  };

  const requestDeviceAccess = async (username: string) => {
    const normalizedUsername = username.toLowerCase();
    try {
      const resp = await fetch(`${API_BASE}/device-request/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: normalizedUsername }),
      });
      const result = await resp.json().catch(() => ({}));
      if (resp.ok) {
        return { ok: true, sourceIp: result.sourceIp };
      }
      return { ok: false, error: result.error || 'Request failed' };
    } catch {
      return { ok: false, error: 'Network error while requesting access' };
    }
  };

  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('session_token');
    // Drop the Realtime socket back to the anon key so it stops receiving
    // authenticated events after logout.
    setRealtimeAuth(null);
    cache.clear();
    try {
      localStorage.removeItem('last_opened_category');
    } catch {
      // Silently ignore if removeItem fails
    }
  }, [cache]);

  // Phase 9: the data client dispatches AUTH_EXPIRED_EVENT when a request is
  // rejected because the session token is missing/expired/invalid. Treat it like
  // an invalid session: log out and tell the user plainly, instead of leaving
  // them staring at silently-empty data.
  useEffect(() => {
    const onAuthExpired = () => {
      logout();
      setSnackbar({
        open: true,
        message: 'Your session expired — please sign in again.',
        severity: 'info',
      });
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired);
  }, [logout]);

  return (
    <WebAuthnContext.Provider
      value={{
        user,
        loading,
        initializing,
        snackbar,
        setSnackbar,
        register,
        authenticate,
        requestDeviceAccess,
        logout,
      }}
    >
      {children}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={confirmDialog.onConfirm}
        onCancel={
          confirmDialog.onCancel ||
          (() => setConfirmDialog({ ...confirmDialog, open: false }))
        }
      />
    </WebAuthnContext.Provider>
  );
};

export const useWebAuthn = () => {
  const context = useContext(WebAuthnContext);
  if (context === undefined) {
    throw new Error('useWebAuthn must be used within a WebAuthnProvider');
  }
  return context;
};
